import type { AnyDomainEvent, CuentaBancoConfig } from '@abastece/shared';
import { orderKey, settingsSchema } from '@abastece/shared';
import type { ApplyResult, Reader } from '../state.js';

const shape = settingsSchema.shape;

/** What the books write themselves, as what they found (`contadas.ts`): nobody sets it. */
const DE_LOS_LIBROS: ReadonlySet<string> = new Set(['cuentasContadas', 'estadosDeCuenta', 'conteosCorregidos']);

export async function reduceConfig(
  event: AnyDomainEvent,
  read: Reader,
): Promise<ApplyResult | null> {
  if (event.type !== 'config.cambiada') return null;

  const { clave, valor } = event.payload;

  if (!Object.prototype.hasOwnProperty.call(shape, clave)) {
    return { effects: [], rejections: [{ eventId: event.id, reason: `Configuración desconocida: ${clave}` }] };
  }
  if (DE_LOS_LIBROS.has(clave)) {
    return { effects: [], rejections: [{ eventId: event.id, reason: `${clave} lo anotan los libros, no se cambia a mano` }] };
  }

  /**
   * Validate the single field rather than the whole settings object: an event that predates
   * a newly-added setting must still apply cleanly during a replay.
   */
  const field = shape[clave as keyof typeof shape];
  const parsed = field.safeParse(valor);
  if (!parsed.success) {
    return {
      effects: [],
      rejections: [{ eventId: event.id, reason: `Valor inválido para ${clave}` }],
    };
  }

  /**
   * The bank accounts go in account by account: one once opened stays on the list, closed when it
   * says `activa: false`. Written whole, a phone that had not heard of an account opened on another
   * wrote the list without it, and every record naming it was refused from then on (2026-10-13).
   */
  let nuevo: unknown = parsed.data;
  if (clave === 'cuentasBanco') {
    const porCuenta = new Map((await read.config()).cuentasBanco.map((c) => [c.cuenta, c]));
    for (const c of parsed.data as CuentaBancoConfig[]) porCuenta.set(c.cuenta, c);
    nuevo = [...porCuenta.values()];
  }

  return {
    effects: [{ kind: 'config.set', clave, valor: nuevo, rev: orderKey(event) }],
    rejections: [],
  };
}
